import { iframeMessenger } from "@/services"
import type { AuthTokenPayload, IframeMessage } from "@/types"
import { useCallback, useEffect, useState } from "react"

interface UseIframeMessagingOptions {
  onMessage?: (message: IframeMessage) => void;
  autoRequestToken?: boolean;
}

interface UseIframeMessagingReturn {
  requestAuthToken: () => void;
  waitForAuthToken: (timeoutMs?: number) => Promise<AuthTokenPayload>;
  sendResize: (height: number, width?: number) => void;
  sendError: (code: string, message: string) => void;
  isInIframe: boolean;
}

/**
 * Hook for iframe communication with parent webapp
 */
export function useIframeMessaging(
  options: UseIframeMessagingOptions = {},
): UseIframeMessagingReturn {
  const { onMessage, autoRequestToken = true } = options
  const [isInIframe] = useState(() => window.parent !== window)

  useEffect(() => {
    // Initialize messenger
    iframeMessenger.init()

    // Subscribe to all messages if callback provided
    let unsubscribe: (() => void) | undefined
    if (onMessage) {
      unsubscribe = iframeMessenger.on("*", onMessage)
    }

    // Auto-request token if enabled
    if (autoRequestToken && isInIframe) {
      iframeMessenger.requestAuthToken()
    }

    return () => {
      unsubscribe?.()
    }
  }, [onMessage, autoRequestToken, isInIframe])

  const requestAuthToken = useCallback(() => {
    iframeMessenger.requestAuthToken()
  }, [])

  const waitForAuthToken = useCallback((timeoutMs?: number) => {
    return iframeMessenger.waitForAuthToken(timeoutMs)
  }, [])

  const sendResize = useCallback((height: number, width?: number) => {
    iframeMessenger.sendResize(height, width)
  }, [])

  const sendError = useCallback((code: string, message: string) => {
    iframeMessenger.sendError(code, message)
  }, [])

  return {
    requestAuthToken,
    waitForAuthToken,
    sendResize,
    sendError,
    isInIframe,
  }
}
